import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const TermsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      id: 'definitions',
      title: '1. Definitions',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>In these Terms and Conditions, the following definitions apply:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>"Company"</strong> means Elite Drainage Services Ltd, registered in England and Wales.</li>
            <li><strong>"Customer"</strong> means the individual or business entering into a service agreement with the Company.</li>
            <li><strong>"Plan"</strong> means the drainage maintenance service agreement selected by the Customer.</li>
            <li><strong>"Covered Works"</strong> means the drainage repair and maintenance services included within the Plan as described in Section 3.</li>
            <li><strong>"Excluded Works"</strong> means works not covered under the Plan as described in Section 4.</li>
            <li><strong>"Claim"</strong> means a request by the Customer for the Company to attend and carry out Covered Works.</li>
            <li><strong>"Activation Date"</strong> means the date on which the Plan becomes active following payment of any applicable setup fee.</li>
            <li><strong>"Waiting Period"</strong> means the 30-day period following the Activation Date during which Claims are restricted as described in Section 7.</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'service-agreement',
      title: '2. Nature of This Agreement',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p><strong>This is a service agreement, not an insurance policy.</strong> Elite Drainage Services Ltd is not an insurance company and this Plan does not constitute insurance. The Plan provides access to drainage maintenance and repair services at a fixed monthly cost, subject to the terms, limits, and exclusions set out in this document.</p>
          <p>This agreement is governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
          <p>By entering into this agreement, the Customer confirms they are aged 18 or over and have the authority to enter into a service agreement for the property in question.</p>
        </div>
      ),
    },
    {
      id: 'covered',
      title: '3. What Is Covered',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>Subject to the exclusions in Section 4 and the claim limits in Section 5, the following services are included in your Plan:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Emergency callout attendance for blocked or collapsed drains within the property boundary</li>
            <li>High-pressure water jetting to clear blockages in underground drainage systems</li>
            <li>Repair or replacement of damaged drain sections up to the claim limits stated in Section 5</li>
            <li>CCTV drain surveys (annual on Monthly Plan; bi-annual on Premium/All-In-One Plan)</li>
            <li>Labour costs for all Covered Works</li>
            <li>Standard parts and materials required to complete Covered Works</li>
            <li>Written service reports following each visit</li>
          </ul>
          <p>All works are subject to inspection and approval by a qualified Elite Drainage Services engineer prior to commencement. The Company reserves the right to decline works that fall outside the scope of this agreement.</p>
        </div>
      ),
    },
    {
      id: 'excluded',
      title: '4. Exclusions — What Is Not Covered',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>The following are expressly excluded from all Plans and will not be covered under this agreement:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Pre-existing damage:</strong> Any damage, defect, or condition that existed prior to the Activation Date of the Plan.</li>
            <li><strong>Neglect or lack of maintenance:</strong> Damage caused by failure to maintain the drainage system or wilful neglect.</li>
            <li><strong>Tree root ingress:</strong> Damage caused by tree root penetration into drainage pipes or structures.</li>
            <li><strong>Subsidence:</strong> Ground movement, subsidence, or heave affecting drainage infrastructure.</li>
            <li><strong>Structural building works:</strong> Drainage works required as part of building extensions, renovations, or new construction.</li>
            <li><strong>Shared or public drains:</strong> Drains that are the responsibility of a water authority, local council, or shared between multiple properties.</li>
            <li><strong>Cosmetic reinstatement:</strong> Reinstatement of surfaces, landscaping, paving, or decorative finishes following drainage works.</li>
            <li><strong>Intentional damage:</strong> Any damage caused deliberately or through gross misuse.</li>
            <li><strong>Flooding and weather damage:</strong> Damage caused by flooding, extreme weather events, or Acts of God.</li>
            <li><strong>Septic tanks and cesspits:</strong> Emptying, repair, or maintenance of septic tanks, cesspits, or sewage treatment plants.</li>
            <li><strong>Gutters and rainwater goods:</strong> Guttering, downpipes, and above-ground rainwater systems.</li>
            <li><strong>Commercial grease traps:</strong> Cleaning or maintenance of commercial grease traps or interceptors.</li>
            <li><strong>Works exceeding claim limits:</strong> Any portion of works exceeding the financial limits set out in Section 5.</li>
            <li><strong>Properties outside service area:</strong> Properties located outside the Company's designated service area (see Section 11).</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'claim-limits',
      title: '5. Claim Limits',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>The following financial limits apply to all Claims under this agreement:</p>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-2">
            <div className="flex justify-between items-center border-b border-orange-100 pb-2">
              <span className="font-semibold text-gray-800">Maximum per individual Claim</span>
              <span className="font-bold text-orange-600">£2,000</span>
            </div>
            <div className="flex justify-between items-center border-b border-orange-100 pb-2">
              <span className="font-semibold text-gray-800">Maximum annual aggregate (per property)</span>
              <span className="font-bold text-orange-600">£10,000</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-800">Maximum callouts per 12-month period</span>
              <span className="font-bold text-orange-600">Subject to fair usage (see Section 6)</span>
            </div>
          </div>
          <p>Works estimated to exceed the per-claim limit will be quoted separately. The Customer may choose to proceed with the excess cost at their own expense. The Company will always provide a written estimate before commencing any works that may exceed the claim limit.</p>
          <p>Claim limits are reviewed annually and may be adjusted upon renewal. Customers will be notified of any changes at least 30 days before their renewal date.</p>
        </div>
      ),
    },
    {
      id: 'fair-usage',
      title: '6. Fair Usage Policy — Callouts',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>All Plans include unlimited reasonable callouts subject to this Fair Usage Policy. The Company reserves the right to review usage patterns and take action where usage is deemed excessive or abusive.</p>
          <p><strong>Fair usage is defined as:</strong> callouts made in good faith for genuine drainage issues at the covered property. The following are considered outside fair usage:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>More than 8 callouts in any rolling 12-month period without a documented recurring fault</li>
            <li>Callouts for issues that are clearly excluded under Section 4</li>
            <li>Repeated callouts for the same issue where the Customer has not followed engineer recommendations</li>
            <li>Callouts for properties other than the registered covered address</li>
          </ul>
          <p>Where the Company determines that usage exceeds fair usage, we will notify the Customer in writing and may, at our discretion, apply a callout charge of £75 per visit for subsequent callouts in that period, or terminate the Plan with 30 days notice.</p>
        </div>
      ),
    },
    {
      id: 'waiting-period',
      title: '7. Waiting Period',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>A <strong>30-day waiting period</strong> applies from the Activation Date of all new Plans. During this period:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Non-emergency Claims will not be accepted or processed.</li>
            <li>Emergency callouts may be attended at the Company's discretion, subject to an emergency assessment fee of £95 which will be waived if the issue is confirmed as a genuine emergency not related to a pre-existing condition.</li>
          </ul>
          <p>The waiting period exists to prevent fraudulent sign-up and immediate claim abuse. It does not affect the Customer's right to cancel during the cooling-off period (see Section 9).</p>
          <p>The waiting period does not apply to Plan renewals or upgrades where the Customer has maintained continuous cover.</p>
        </div>
      ),
    },
    {
      id: 'payments',
      title: '8. Payments & Missed Payments',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p><strong>Setup Fee:</strong> Where applicable, a one-time activation and onboarding fee is payable before the Plan becomes active. This fee covers the initial property inspection, system registration, and account setup. The setup fee is non-refundable after the cooling-off period.</p>
          <p><strong>Monthly Payments:</strong> Monthly plan fees are collected by Direct Debit or card on the same date each month. Payments are due in advance.</p>
          <p><strong>Missed Payments:</strong></p>
          <ul className="list-disc pl-5 space-y-2">
            <li>If a payment is missed, the Company will attempt to collect payment again within 5 working days.</li>
            <li>If payment remains outstanding after 10 working days, the Plan will be suspended and no new Claims will be accepted.</li>
            <li>If payment remains outstanding after 30 days, the Plan will be terminated and any outstanding balance will remain payable.</li>
            <li>Reinstatement of a suspended Plan may be subject to a reinstatement fee of £25.</li>
            <li>Outstanding balances may be referred to a debt collection agency after 60 days.</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'cooling-off',
      title: '9. Cooling-Off Period & Cancellation',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p><strong>Cooling-Off Period:</strong> You have a <strong>14-day cooling-off period</strong> from the date your Plan is activated. During this period, you may cancel your Plan for any reason and receive a full refund of any monthly fees paid. The setup fee will be refunded in full provided no services have been delivered during the cooling-off period.</p>
          <p><strong>Cancellation After Cooling-Off Period:</strong></p>
          <ul className="list-disc pl-5 space-y-2">
            <li>You may cancel your Plan at any time by providing 30 days written notice to info@elitedrain-services.com or by calling 08000487055.</li>
            <li>Cover continues until the end of the 30-day notice period.</li>
            <li>No refunds are issued for the setup fee after the cooling-off period.</li>
            <li>No refunds are issued for any portion of the current monthly payment period.</li>
            <li>Any outstanding balances remain payable upon cancellation.</li>
            <li>Cover ceases entirely on the cancellation date. No Claims will be accepted after this date.</li>
          </ul>
          <p><strong>Cancellation by the Company:</strong> The Company reserves the right to cancel this agreement with 30 days written notice. In the event of cancellation by the Company without cause, a pro-rata refund of any prepaid monthly fees will be issued.</p>
        </div>
      ),
    },
    {
      id: 'renewal',
      title: '10. Renewal Terms',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>Plans renew automatically on a monthly basis unless cancelled in accordance with Section 9. The Company will notify Customers of any changes to plan pricing or terms at least 30 days before the renewal date.</p>
          <p>Customers may upgrade or downgrade their Plan at any time. Changes will take effect from the next billing date. No waiting period applies to upgrades where continuous cover has been maintained.</p>
          <p>The Company reserves the right to adjust pricing annually in line with inflation (RPI). Customers will be notified of any price increases at least 30 days in advance.</p>
        </div>
      ),
    },
    {
      id: 'geographic',
      title: '11. Geographic Coverage',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>Plans are available for properties located within the Company's designated service area, which currently covers:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Bristol and surrounding areas (BS postcode districts)</li>
            <li>Bournemouth, Poole, and Dorset (BH, DT postcode districts)</li>
            <li>Southampton and Hampshire (SO, PO, SP postcode districts)</li>
            <li>Wiltshire (SN, BA postcode districts)</li>
            <li>Somerset (TA, BS postcode districts)</li>
          </ul>
          <p>Coverage is limited to <strong>UK Mainland only</strong>. Properties in Northern Ireland, the Scottish Highlands and Islands, the Channel Islands, and the Isle of Man are not currently covered.</p>
          <p>If you are unsure whether your property falls within our service area, please contact us before purchasing a Plan. Plans purchased for properties outside our service area will be refunded in full.</p>
        </div>
      ),
    },
    {
      id: 'liability',
      title: '12. Limitation of Liability',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>The Company's total liability to the Customer under this agreement shall not exceed the total amount paid by the Customer in the 12 months preceding the event giving rise to the claim.</p>
          <p>The Company shall not be liable for any indirect, consequential, or economic loss, including but not limited to loss of earnings, loss of use of property, or damage to contents caused by drainage failure.</p>
          <p>Nothing in these Terms limits the Company's liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.</p>
        </div>
      ),
    },
    {
      id: 'data',
      title: '13. Data Protection',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>The Company processes personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. Personal data collected in connection with this agreement will be used solely for the purpose of providing the services described herein.</p>
          <p>The Company will not sell or share personal data with third parties for marketing purposes. Data may be shared with subcontractors where necessary to deliver services, subject to appropriate data processing agreements.</p>
          <p>For full details, please refer to our Privacy Policy available on our website.</p>
        </div>
      ),
    },
    {
      id: 'changes',
      title: '14. Changes to These Terms',
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>The Company reserves the right to update these Terms and Conditions at any time. Customers will be notified of material changes at least 30 days before they take effect. Continued use of the Plan after the effective date of changes constitutes acceptance of the updated terms.</p>
          <p>The current version of these Terms and Conditions is always available on our website. The date of last update is shown at the top of this page.</p>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-900 py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-400 text-sm mb-8 transition-colors cursor-pointer">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-orange-500/20 rounded-xl">
              <i className="ri-file-text-line text-orange-400 text-2xl"></i>
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white">Terms &amp; Conditions</h1>
              <p className="text-gray-400 text-sm mt-1">Elite Drainage Services Ltd — Service Agreement</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-6 text-xs text-gray-500">
            <span>Last updated: April 2026</span>
            <span>•</span>
            <span>Version 2.1</span>
            <span>•</span>
            <span>Applies to all Plans from 1 April 2026</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-16">
        {/* Important notice */}
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6 mb-12">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-xl flex-shrink-0">
              <i className="ri-information-line text-orange-500 text-xl"></i>
            </div>
            <div>
              <h3 className="text-gray-900 font-bold text-base mb-2">Important — Please Read</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                This document forms the entire agreement between you and Elite Drainage Services Ltd. Please read it carefully before purchasing a Plan. By purchasing a Plan, you confirm that you have read, understood, and agree to these Terms and Conditions. If you have any questions, please contact us before purchasing.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mt-2">
                <strong>This is a service agreement, not an insurance policy.</strong> Cover is subject to the exclusions and limits described below.
              </p>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-gray-50 rounded-2xl p-6 mb-12">
          <h2 className="text-gray-900 font-bold text-base mb-4">Contents</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="text-sm text-orange-500 hover:text-orange-600 hover:underline transition-colors cursor-pointer"
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-12">
          {sections.map((s) => (
            <div key={s.id} id={s.id} className="scroll-mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">{s.title}</h2>
              {s.content}
            </div>
          ))}
        </div>

        {/* Company Info */}
        <div className="mt-16 bg-gray-900 rounded-2xl p-8">
          <h3 className="text-white font-bold text-lg mb-4">Company Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Registered Company Name</div>
              <div className="text-white font-semibold">Elite Drainage Services Ltd</div>
            </div>
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Registered in</div>
              <div className="text-white font-semibold">England and Wales</div>
            </div>
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Registered Office</div>
              <div className="text-white font-semibold">South &amp; South West England</div>
            </div>
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Contact</div>
              <div className="text-white font-semibold">info@elitedrain-services.com</div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
